import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';

import api from '../services/api';

const EXTRA_WEIGHT_RATE = 75;
const EXPRESS_EXTRA_WEIGHT_RATE = 150;

const REGIONS = {
  COLOMBO: 'Colombo 1 – 15',
  WESTERN: 'Western Province',
  OTHER: 'All Other Areas',
  RETURN: 'Return',
};

const PARCEL_TYPES = {
  NORMAL: 'Normal',
  FRAGILE: 'Glass / Fragile Items',
};

const PARCEL_SIZES = {
  STANDARD: 'Standard-sized (0 to 1.5 sq. ft)',
  SAMY_BULK: 'Samy Bulk (1.51 to 2.5 sq. ft)',
};

const DELIVERY_SERVICES = {
  NORMAL: {
    type: 'normal',
    label: 'Normal Delivery',
  },
  ONE_HOUR: {
    type: 'one_hour',
    label: 'One Hour Delivery',
  },
  SAME_DAY: {
    type: 'same_day',
    label: 'Same Day Delivery',
  },
};

const DELIVERY_RATES = {
  [PARCEL_SIZES.STANDARD]: {
    [REGIONS.COLOMBO]: {
      [PARCEL_TYPES.NORMAL]: 325,
      [PARCEL_TYPES.FRAGILE]: 450,
    },
    [REGIONS.WESTERN]: {
      [PARCEL_TYPES.NORMAL]: 400,
      [PARCEL_TYPES.FRAGILE]: 500,
    },
    [REGIONS.OTHER]: {
      [PARCEL_TYPES.NORMAL]: 550,
      [PARCEL_TYPES.FRAGILE]: 650,
    },
    [REGIONS.RETURN]: {
      [PARCEL_TYPES.NORMAL]: 200,
      [PARCEL_TYPES.FRAGILE]: 300,
    },
  },

  [PARCEL_SIZES.SAMY_BULK]: {
    [REGIONS.COLOMBO]: {
      [PARCEL_TYPES.NORMAL]: 600,
      [PARCEL_TYPES.FRAGILE]: 850,
    },
    [REGIONS.WESTERN]: {
      [PARCEL_TYPES.NORMAL]: 700,
      [PARCEL_TYPES.FRAGILE]: 950,
    },
    [REGIONS.OTHER]: {
      [PARCEL_TYPES.NORMAL]: 850,
      [PARCEL_TYPES.FRAGILE]: 1000,
    },
    [REGIONS.RETURN]: {
      [PARCEL_TYPES.NORMAL]: 600,
      [PARCEL_TYPES.FRAGILE]: 850,
    },
  },
};

const EXPRESS_DELIVERY_RATES = {
  [DELIVERY_SERVICES.ONE_HOUR.type]: {
    [PARCEL_TYPES.NORMAL]: 700,
    [PARCEL_TYPES.FRAGILE]: 850,
  },
  [DELIVERY_SERVICES.SAME_DAY.type]: {
    [PARCEL_TYPES.NORMAL]: 600,
    [PARCEL_TYPES.FRAGILE]: 750,
  },
};

const weightOptions = Array.from({ length: 20 }, (_, index) => index + 1);

const getDeliveryServiceLabel = (serviceType) => {
  const service = Object.values(DELIVERY_SERVICES).find(
    (item) => item.type === serviceType
  );

  return service?.label || DELIVERY_SERVICES.NORMAL.label;
};

const isExpressDelivery = (serviceType) =>
  serviceType === DELIVERY_SERVICES.ONE_HOUR.type ||
  serviceType === DELIVERY_SERVICES.SAME_DAY.type;

const isExpressAllowedRegion = (region) => region === REGIONS.COLOMBO;

export default function OrderForm() {
  const { sellerId } = useParams();

  const [form, setForm] = useState({
    customerName: '',
    customerAddress: '',
    customerPhone: '',
    itemName: '',
    price: '',
    weightKg: 1,
    deliveryRegion: REGIONS.COLOMBO,
    deliveryServiceType: DELIVERY_SERVICES.NORMAL.type,
    parcelType: PARCEL_TYPES.NORMAL,
    parcelSize: PARCEL_SIZES.STANDARD,
    deliveryAddress: '',
    paymentType: 'COD',
    paid: false,
    bankSlip: null,
  });

  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const pricing = useMemo(() => {
    const itemPrice = Number(form.price || 0);
    const weightKg = Number(form.weightKg || 1);
    const deliveryServiceType = form.deliveryServiceType || DELIVERY_SERVICES.NORMAL.type;
    const deliveryServiceLabel = getDeliveryServiceLabel(deliveryServiceType);

    const expressSelected = isExpressDelivery(deliveryServiceType);

    if (expressSelected) {
      const baseRate =
        EXPRESS_DELIVERY_RATES?.[deliveryServiceType]?.[form.parcelType] || 0;

      const extraWeightCharge =
        weightKg <= 1 ? 0 : Math.max(weightKg - 1, 0) * EXPRESS_EXTRA_WEIGHT_RATE;

      const deliveryFee = baseRate + extraWeightCharge;
      const totalAmount = itemPrice + deliveryFee;

      return {
        itemPrice,
        weightKg,
        deliveryServiceType,
        deliveryServiceLabel,
        baseRate,
        extraWeightCharge,
        deliveryFee,
        totalAmount,
      };
    }

    // IMPORTANT:
    // Normal delivery calculation is kept exactly as existing logic.
    const baseRate =
      DELIVERY_RATES?.[form.parcelSize]?.[form.deliveryRegion]?.[form.parcelType] || 0;

    const extraWeightCharge = Math.max(weightKg - 1, 0) * EXTRA_WEIGHT_RATE;
    const deliveryFee = baseRate + extraWeightCharge;
    const totalAmount = itemPrice + deliveryFee;

    return {
      itemPrice,
      weightKg,
      deliveryServiceType,
      deliveryServiceLabel,
      baseRate,
      extraWeightCharge,
      deliveryFee,
      totalAmount,
    };
  }, [
    form.price,
    form.weightKg,
    form.deliveryRegion,
    form.deliveryServiceType,
    form.parcelType,
    form.parcelSize,
  ]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => {
      const nextForm = {
        ...prev,
        [name]: value,
      };

      if (
        name === 'deliveryRegion' &&
        !isExpressAllowedRegion(value) &&
        isExpressDelivery(prev.deliveryServiceType)
      ) {
        nextForm.deliveryServiceType = DELIVERY_SERVICES.NORMAL.type;
      }

      return nextForm;
    });
  };

  const handlePaymentChange = (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      paymentType: value,
      paid: value !== 'COD',
    }));
  };

  const handleFileChange = (event) => {
    setForm((prev) => ({
      ...prev,
      bankSlip: event.target.files?.[0] || null,
    }));
  };

  const resetForm = () => {
    setForm({
      customerName: '',
      customerAddress: '',
      customerPhone: '',
      itemName: '',
      price: '',
      weightKg: 1,
      deliveryRegion: REGIONS.COLOMBO,
      deliveryServiceType: DELIVERY_SERVICES.NORMAL.type,
      parcelType: PARCEL_TYPES.NORMAL,
      parcelSize: PARCEL_SIZES.STANDARD,
      deliveryAddress: '',
      paymentType: 'COD',
      paid: false,
      bankSlip: null,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess('');
    setError('');

    if (!sellerId) {
      setError('Invalid seller link. Seller ID is missing.');
      return;
    }

    if (
      !form.customerName ||
      !form.customerPhone ||
      !form.customerAddress ||
      !form.itemName ||
      !form.price ||
      !form.weightKg ||
      !form.deliveryRegion ||
      !form.deliveryServiceType ||
      !form.parcelType ||
      !form.parcelSize ||
      !form.deliveryAddress
    ) {
      setError('Please fill all required fields.');
      return;
    }

    if (pricing.itemPrice <= 0) {
      setError('Please enter a valid item price.');
      return;
    }

    if (
      isExpressDelivery(form.deliveryServiceType) &&
      !isExpressAllowedRegion(form.deliveryRegion)
    ) {
      setError('One Hour Delivery and Same Day Delivery are available only for Colombo 1 – 15.');
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();

      formData.append('customerName', form.customerName);
      formData.append('customerAddress', form.customerAddress);
      formData.append('customerPhone', form.customerPhone);
      formData.append('itemName', form.itemName);

      formData.append('price', pricing.itemPrice);
      formData.append('weightKg', pricing.weightKg);
      formData.append('deliveryRegion', form.deliveryRegion);
      formData.append('delivery_service_type', pricing.deliveryServiceType);
      formData.append('delivery_service_label', pricing.deliveryServiceLabel);
      formData.append('parcelType', form.parcelType);
      formData.append('parcelSize', form.parcelSize);
      formData.append('base_delivery_rate', pricing.baseRate);
      formData.append('extra_weight_charge', pricing.extraWeightCharge);
      formData.append('delivery_fee', pricing.deliveryFee);
      formData.append('total_amount', pricing.totalAmount);

      formData.append('deliveryAddress', form.deliveryAddress);
      formData.append('paymentType', form.paymentType);
      formData.append('paid', form.paid ? 'true' : 'false');

      if (form.bankSlip) {
        formData.append('bankSlip', form.bankSlip);
      }

      await api.post(`/seller/${sellerId}/orders`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess('Order submitted successfully. The seller will process your delivery.');
      resetForm();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to submit order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const expressOptionsDisabled = !isExpressAllowedRegion(form.deliveryRegion);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f6f7fb',
        py: { xs: 3, md: 6 },
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            borderRadius: 5,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            boxShadow: '0 20px 60px rgba(15,23,42,0.08)',
          }}
        >
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              color: '#fff',
              background:
                'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #2563eb 100%)',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 58,
                  height: 58,
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.16)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ReceiptLongOutlinedIcon sx={{ fontSize: 34 }} />
              </Box>

              <Box>
                <Stack direction="row" spacing={1} sx={{ mb: 0.8 }}>
                  <Chip
                    size="small"
                    label="Customer Order Form"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.18)',
                      color: '#fff',
                      fontWeight: 800,
                    }}
                  />

                  <Chip
                    size="small"
                    label={`Seller ID: ${sellerId || '-'}`}
                    sx={{
                      bgcolor: 'rgba(34,197,94,0.22)',
                      color: '#DCFCE7',
                      fontWeight: 800,
                    }}
                  />
                </Stack>

                <Typography variant="h4" fontWeight={900}>
                  Submit Delivery Order
                </Typography>

                <Typography sx={{ mt: 0.6, color: 'rgba(255,255,255,0.78)' }}>
                  Fill your delivery, parcel, and payment details carefully.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 3, md: 4 } }}>
            {success ? (
              <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
                {success}
              </Alert>
            ) : null}

            {error ? (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                {error}
              </Alert>
            ) : null}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" fontWeight={900} gutterBottom>
                    Customer Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Customer Name"
                        name="customerName"
                        value={form.customerName}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonOutlineIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Customer Phone"
                        name="customerPhone"
                        value={form.customerPhone}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        multiline
                        minRows={2}
                        label="Customer Address"
                        name="customerAddress"
                        value={form.customerAddress}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <HomeOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="h6" fontWeight={900} gutterBottom>
                    Parcel Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <TextField
                        fullWidth
                        required
                        label="Item Name"
                        name="itemName"
                        value={form.itemName}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Inventory2OutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        required
                        type="number"
                        label="Item Price"
                        name="price"
                        value={form.price}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              LKR
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        select
                        label="Weight"
                        name="weightKg"
                        value={form.weightKg}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <ScaleOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                      >
                        {weightOptions.map((weight) => (
                          <MenuItem key={weight} value={weight}>
                            {weight}kg
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        select
                        label="Delivery Region"
                        name="deliveryRegion"
                        value={form.deliveryRegion}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOnOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                      >
                        {Object.values(REGIONS).map((region) => (
                          <MenuItem key={region} value={region}>
                            {region}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        select
                        label="Delivery Service"
                        name="deliveryServiceType"
                        value={form.deliveryServiceType}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocalShippingOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                        helperText={
                          expressOptionsDisabled
                            ? 'Express delivery is available only for Colombo 1 – 15.'
                            : 'Choose normal, one hour, or same day delivery.'
                        }
                      >
                        <MenuItem value={DELIVERY_SERVICES.NORMAL.type}>
                          {DELIVERY_SERVICES.NORMAL.label}
                        </MenuItem>

                        <MenuItem
                          value={DELIVERY_SERVICES.ONE_HOUR.type}
                          disabled={expressOptionsDisabled}
                        >
                          {DELIVERY_SERVICES.ONE_HOUR.label}
                        </MenuItem>

                        <MenuItem
                          value={DELIVERY_SERVICES.SAME_DAY.type}
                          disabled={expressOptionsDisabled}
                        >
                          {DELIVERY_SERVICES.SAME_DAY.label}
                        </MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        select
                        label="Parcel Type"
                        name="parcelType"
                        value={form.parcelType}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CategoryOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                      >
                        {Object.values(PARCEL_TYPES).map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        select
                        label="Parcel Size"
                        name="parcelSize"
                        value={form.parcelSize}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <StraightenOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                      >
                        {Object.values(PARCEL_SIZES).map((size) => (
                          <MenuItem key={size} value={size}>
                            {size}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        multiline
                        minRows={2}
                        label="Delivery Address"
                        name="deliveryAddress"
                        value={form.deliveryAddress}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocalShippingOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="h6" fontWeight={900} gutterBottom>
                    Payment Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Payment Type"
                        name="paymentType"
                        value={form.paymentType}
                        onChange={handlePaymentChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PaymentsOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                      >
                        <MenuItem value="COD">COD</MenuItem>
                        <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                        <MenuItem value="Card">Card</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Button
                        fullWidth
                        variant="outlined"
                        component="label"
                        sx={{
                          height: '56px',
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 800,
                        }}
                      >
                        Upload Bank Slip
                        <input
                          hidden
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handleFileChange}
                        />
                      </Button>

                      {form.bankSlip ? (
                        <Typography variant="caption" color="text.secondary">
                          Selected: {form.bankSlip.name}
                        </Typography>
                      ) : null}
                    </Grid>
                  </Grid>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 4,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <Stack spacing={1.3}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary" fontWeight={700}>
                        Item Price
                      </Typography>
                      <Typography fontWeight={900}>
                        LKR {pricing.itemPrice.toFixed(2)}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary" fontWeight={700}>
                        Delivery Service
                      </Typography>
                      <Chip
                        size="small"
                        label={pricing.deliveryServiceLabel}
                        color={isExpressDelivery(pricing.deliveryServiceType) ? 'warning' : 'default'}
                        sx={{ fontWeight: 900 }}
                      />
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary" fontWeight={700}>
                        Base Delivery Rate
                      </Typography>
                      <Typography fontWeight={900}>
                        LKR {pricing.baseRate.toFixed(2)}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary" fontWeight={700}>
                        Extra Weight Charge
                      </Typography>
                      <Typography fontWeight={900}>
                        LKR {pricing.extraWeightCharge.toFixed(2)}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary" fontWeight={700}>
                        Delivery Fee
                      </Typography>
                      <Typography fontWeight={900}>
                        LKR {pricing.deliveryFee.toFixed(2)}
                      </Typography>
                    </Stack>

                    <Divider />

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h6" fontWeight={900}>
                        Total Amount
                      </Typography>
                      <Typography variant="h6" fontWeight={900} color="primary">
                        LKR {pricing.totalAmount.toFixed(2)}
                      </Typography>
                    </Stack>

                    {isExpressDelivery(form.deliveryServiceType) ? (
                      <Alert severity="success" sx={{ mt: 1, borderRadius: 3 }}>
                        {pricing.deliveryServiceLabel} selected. Express delivery is available only for Colombo 1 – 15.
                      </Alert>
                    ) : null}

                    {form.parcelType === PARCEL_TYPES.FRAGILE ? (
                      <Alert severity="warning" sx={{ mt: 1, borderRadius: 3 }}>
                        This order will be marked as FRAGILE / GLASS ITEM on the waybill.
                      </Alert>
                    ) : null}

                    {form.paymentType === 'COD' ? (
                      <Alert severity="info" sx={{ mt: 1, borderRadius: 3 }}>
                        Customer must pay total amount: LKR {pricing.totalAmount.toFixed(2)} on delivery.
                      </Alert>
                    ) : null}
                  </Stack>
                </Paper>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  sx={{
                    py: 1.5,
                    borderRadius: 3,
                    fontWeight: 900,
                    textTransform: 'none',
                    boxShadow: 'none',
                  }}
                >
                  {submitting
                    ? 'Submitting...'
                    : `Submit Order - LKR ${pricing.totalAmount.toFixed(2)}`}
                </Button>
              </Stack>
            </form>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}